import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
    ApiResponseError,
    handleOnError,
    ToastType,
    useApiRequest
} from 'contexts/apiRequestContext';
import { useIdentity } from 'contexts/identityContext';
import { handleErrorUseQuery } from 'helpers';
import {
    getDataprevMarginQueryAsync,
    postUpdateDataprevMarginQueryAsync
} from 'services/DataprevEmployee/DataprevEmployee';
import { DataprevMarginQueryUpdateModel, TUpdateDataprevProps } from 'services/zetra';

export function useUpdateDataprevMarginQuery(
    onSuccess?: (data: DataprevMarginQueryUpdateModel) => void,
    onError?: (error: ApiResponseError) => void
) {
    const { token } = useIdentity();
    const { startRequest, endRequest, setSubmitError } = useApiRequest();

    const { mutateAsync, isLoading, error } = useMutation({
        mutationFn: async (value: TUpdateDataprevProps) => {
            startRequest();
            const { data, status, statusText } = await postUpdateDataprevMarginQueryAsync(
                value,
                token!
            );

            if (isAxiosError(data)) {
                setSubmitError({
                    type: 'error',
                    code: status + '' + statusText,
                    message: data.message,
                    errors: data.response?.data?.errors
                });
                throw data;
            }

            if (status >= 400 && status <= 599) {
                throw data;
            }

            endRequest(true);
            return data;
        },
        onSuccess,
        onError(error, _) {
            handleErrorUseQuery(
                error,
                setSubmitError,
                endRequest,
                () => onError && onError(error as ApiResponseError)
            );
        }
    });

    return { mutateAsync, isLoading, error };
}

export function useDataprevMarginQuery(
    params: TUpdateDataprevProps,
    enabledRequest = false,
    setToast: React.Dispatch<React.SetStateAction<ToastType>>
) {
    const { token } = useIdentity();
    const { startRequest, endRequest, setSubmitError } = useApiRequest();
    const { personId, registrationNumber, creditProductId } = params;
    const filter = !!personId || !!registrationNumber || !!creditProductId;

    return useQuery({
        enabled: !!token && enabledRequest && filter,
        refetchIntervalInBackground: false,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
        retry: false,
        queryKey: ['Dataprev-margin-query', filter],
        queryFn: async () => {
            startRequest();
            try {
                const { data } = await getDataprevMarginQueryAsync(params, token!);
                endRequest(true);
                return data;
            } catch (err) {
                endRequest(true);

                if (isAxiosError(err)) {
                    setSubmitError({
                        type: 'error',
                        code: err.response?.status + '' + err.response?.statusText,
                        message: err.response?.data?.message,
                        errors: err.response?.data?.message
                    });
                    throw err;
                }

                throw err;
            }
        },
        onError(err) {
            handleOnError(err as ApiResponseError, setToast);
        }
    });
}
